import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Popover,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import { HexColorPicker } from 'react-colorful';

import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useAssistant } from '@core/assistant/providers/Provider';
import { triggerMatchesRegion } from '@core/assistant/providers/providerUtils';
import { useMytaverse } from '@providers/MytaverseProvider';
import type {
  ISpawnableObject,
  ISpawnableObjectProperty,
} from '@interfaces/event';
import {
  buildRealtimeEntitySnapshot,
  isRealtimeEntityDirty,
  type RealtimeEntityPropertyValue,
} from '@interfaces/realtimeEntities';
import SpawnedEntitiesService from '@services/SpawnedEntitiesService';
import DynamicMapTriggersService from '@services/DynamicMapTriggersService';
import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import type { IAssistantScript } from '@shared/interfaces/assistantScript';
import type { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';
import {
  assistantMapKey,
  type IAssistantMapRef,
} from '@shared/utils/assistantMap';

import { useEditorMode } from '../EditorModeContext';
import { useSpawnableObjects } from '../useSpawnableObjects';
import {
  deleteRealtimeEntityInUnreal,
  endRealtimeEntityEditInUnreal,
  startRealtimeEntityEditInUnreal,
  updateRealtimeEntityInUnreal,
} from '../realtimeEntitiesUnreal';
import {
  declaresTriggerName,
  readTriggerName,
  SCRIPTABLE_REGION_TYPES,
  TRIGGER_NAME_PROPERTY,
} from '../triggerRegions';
import { filterByScope, tagMatchesTrigger } from '../AiScripts/aggregate';
import { useEditorAiData } from '../AiScripts/EditorAiDataContext';
import { useRegionIndex } from '../AiScripts/useRegionIndex';
import { useInspectorStyles } from './styles';
import { deriveAxesInputs, toTuple } from './axesInputs';
import {
  colorTupleToHex,
  hexToColorChannels,
  isByteScaleColor,
} from './colorChannels';

const SCALE_DEFAULT = 1;

type TransformGroup = 'position' | 'rotation' | 'scale';

const TRANSFORM_GROUP_CONFIG: Record<
  TransformGroup,
  { fallback: number; axisLabels: [string, string, string] }
> = {
  position: { fallback: 0, axisLabels: ['X', 'Y', 'Z'] },
  rotation: { fallback: 0, axisLabels: ['Pitch', 'Yaw', 'Roll'] },
  scale: { fallback: SCALE_DEFAULT, axisLabels: ['X', 'Y', 'Z'] },
};

/**
 * Tells whether a schema property goes over the wire as a number array.
 *
 * @param property - Schema entry for the property.
 */
const isStructProperty = (property: ISpawnableObjectProperty): boolean =>
  property.type === 'vector' ||
  property.type === 'rotator' ||
  property.type === 'color';

/**
 * Resolves how many array components a struct property carries. Colors keep
 * the arity of their stored value — `[R, G, B, A]` stays four so an edit never
 * silently strips the alpha the value was spawned with — and everything else
 * is a 3-tuple.
 *
 * @param property - Schema entry for the property.
 * @param stored - The value currently held for it, possibly absent.
 */
const structAxisCount = (
  property: ISpawnableObjectProperty,
  stored: RealtimeEntityPropertyValue | undefined,
): number =>
  property.type === 'color' && Array.isArray(stored) && stored.length === 4
    ? 4
    : 3;

/**
 * Names the axes of a struct property — rotators use their FRotator names,
 * colors their channels.
 *
 * @param property - Schema entry for the property.
 * @param axisCount - How many components the property carries.
 */
const structAxisLabels = (
  property: ISpawnableObjectProperty,
  axisCount: number,
): string[] => {
  if (property.type === 'rotator') {
    return ['Pitch', 'Yaw', 'Roll'];
  }
  if (property.type === 'color') {
    return axisCount === 4 ? ['R', 'G', 'B', 'A'] : ['R', 'G', 'B'];
  }
  return ['X', 'Y', 'Z'];
};

/**
 * Seeds the raw input strings for one property from the entity's stored value.
 * Struct properties get one entry per component (`name.0` …), scalars one.
 *
 * @param property - Schema entry for the property.
 * @param stored - The value currently held for it, possibly absent.
 */
const seedPropertyInputs = (
  property: ISpawnableObjectProperty,
  stored: RealtimeEntityPropertyValue | undefined,
): Record<string, string> => {
  if (isStructProperty(property)) {
    const tuple = Array.isArray(stored) ? stored : [];
    const axisCount = structAxisCount(property, stored);
    const inputs: Record<string, string> = {};
    for (let axis = 0; axis < axisCount; axis += 1) {
      inputs[`${property.name}.${axis}`] = String(tuple[axis] ?? 0);
    }
    return inputs;
  }
  if (property.type === 'bool') {
    return { [property.name]: stored === true ? 'true' : 'false' };
  }
  return { [property.name]: stored === undefined ? '' : String(stored) };
};

/**
 * Seeds the raw input strings for every property of a schema at once.
 *
 * @param schema - The entity's type schema, possibly not yet resolved.
 * @param properties - The entity's stored property values.
 */
const seedAllPropertyInputs = (
  schema: ISpawnableObject | undefined,
  properties: Record<string, RealtimeEntityPropertyValue> | undefined,
): Record<string, string> =>
  (schema?.properties ?? []).reduce<Record<string, string>>(
    (inputs, property) => ({
      ...inputs,
      ...seedPropertyInputs(property, properties?.[property.name]),
    }),
    {},
  );

interface RealtimeEntityInspectorProps {
  entityId: string;
}

interface ColorPropertyControlProps {
  label: string;
  /** Current colour as `#rrggbb`. */
  hex: string;
  onChange: (hex: string) => void;
  testId: string;
}

/**
 * Swatch that opens a colour picker, the same control the map colours use.
 *
 * Its own component because the popover needs state, and a hook inside the
 * property loop would change order with the schema.
 *
 * @param props.label - The property's `displayName`.
 * @param props.hex - Current colour as `#rrggbb`.
 * @param props.onChange - Called with the picked colour on every change.
 * @param props.testId - Test id for the swatch row.
 */
const ColorPropertyControl = ({
  label,
  hex,
  onChange,
  testId,
}: ColorPropertyControlProps) => {
  const styles = useInspectorStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hexInputRef = usePreventGameKeydownListening<HTMLInputElement>();

  return (
    <>
      <Box sx={styles.colorRow} data-testid={testId}>
        <Box
          sx={[styles.colorSwatch, { backgroundColor: hex }]}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        />
        <Box sx={styles.colorRowText}>
          <Typography sx={styles.colorRowLabel}>{label}</Typography>
          <Typography sx={styles.colorRowHex}>{hex}</Typography>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: styles.pickerPopover }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <TextField
            inputRef={hexInputRef}
            size="small"
            autoComplete="off"
            value={hex}
            onChange={(e) => {
              const raw = e.target.value.trim();
              const next = raw.startsWith('#') ? raw : `#${raw}`;

              if (/^#[0-9a-f]{6}$/i.test(next)) {
                onChange(next);
              }
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            sx={styles.pickerHexField}
            inputProps={{ maxLength: 7, 'data-testid': `${testId}-hex` }}
          />
          <HexColorPicker color={hex} onChange={onChange} />
        </Box>
      </Popover>
    </>
  );
};

/**
 * Whether a property needs a label rendered above its control.
 *
 * A checkbox carries its name beside itself, and struct and colour controls
 * label themselves; everything else used MUI's floating label, which sits
 * off-centre against this panel's tightened input padding and truncates a long
 * name inside the field.
 *
 * @param property - Schema entry for the property.
 * @returns True when the caller must render the label.
 */
const needsExternalLabel = (property: ISpawnableObjectProperty): boolean =>
  property.type !== 'bool' && !isStructProperty(property);

/**
 * Whether a failed request says the row is not there — a delete of something
 * never saved, or one another admin already removed.
 *
 * @param error - Rejection from the REST client.
 * @returns True when the backend answered 404.
 */
const isMissingOnBackend = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404;

/**
 * Treats a delete of something already gone as done — a retry after a partial
 * cascade, or another admin ahead of this one — while letting every other
 * failure through.
 *
 * @param request - The delete in flight.
 * @returns The same request, settled instead of rejected on a 404.
 */
const settledIfMissing = <T,>(request: Promise<T>): Promise<T | undefined> =>
  request.catch((error: unknown) => {
    if (isMissingOnBackend(error)) {
      return undefined;
    }
    throw error;
  });

/**
 * Reads a script's triggers as a list whatever shape the record stores.
 *
 * @param script - Assistant script record.
 * @returns Its trigger names, never a bare string.
 */
const triggersOf = (script: IAssistantScript): string[] =>
  (Array.isArray(script.scriptTrigger)
    ? script.scriptTrigger
    : [script.scriptTrigger]
  ).filter(Boolean);

/**
 * Why the delete cascade must not run, when it must not.
 *
 * `sharedName`: another region answers to the same name, so the script still
 * serves it. `multiTrigger`: the script also serves other triggers.
 * `noBranding`: with no configuration selected the script lookup is unscoped
 * and could reach another configuration's script.
 */
type CascadeBlocker = 'sharedName' | 'multiTrigger' | 'noBranding';

/**
 * Edits one live-spawned entity: transform fields plus a form built from the
 * chosen type's `properties` schema, every edit sent immediately as an
 * UPDATE_REALTIME_ENTITY carrying only the changed field. A blur that changed
 * nothing sends nothing, and an unparseable field reverts to the stored value
 * rather than committing a fallback the user never typed.
 *
 * The engine lives with those edits at once; the backend only gets what Save
 * writes, so an entity is dirty until its snapshot matches the last save.
 *
 * Runs the gizmo edit session for as long as a tracked entity is selected —
 * START_REALTIME_ENTITY_EDIT on mount, END_ on unmount — relying on
 * `key={entityId}` in Inspector.tsx to remount on selection switches, the same
 * lifecycle HotspotInspector uses.
 *
 * @param props.entityId - Entity being edited; its live state is in the
 * realtimeEntities registry, its saved snapshot on the open branding.
 */
const RealtimeEntityInspector: React.FC<RealtimeEntityInspectorProps> = ({
  entityId,
}) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const rootRef = usePreventGameKeydownListening<HTMLDivElement>();

  const { sendMessageToUnreal } = useMytaverse();
  const {
    realtimeEntities,
    upsertRealtimeEntity,
    removeRealtimeEntity,
    realtimeEntityLiveTransform,
    currentRoom,
    currentBrandingId,
    dynamicAiTriggers,
    setDynamicAiTriggers,
  } = useMytaverseEvent();
  const { clearSelection, setAutosaveStatus, openRegionScript } =
    useEditorMode();
  const { types } = useSpawnableObjects();
  const assistant = useAssistant();
  const { scripts, ragItems, refetchAll } = useEditorAiData();

  const entity = realtimeEntities.find(
    (candidate) => candidate.entityId === entityId,
  );
  const schema: ISpawnableObject | undefined = types.find(
    (candidate) => candidate.typeId === entity?.typeId,
  );
  const schemaProperties = schema?.properties ?? [];
  const entityExists = !!entity;
  // Resolved the way the branding-spot and hotspot fetches resolve it: a saved
  // entity belongs to the configuration the author has open.
  const brandingId = currentBrandingId ?? currentRoom?.activeBrandingId ?? null;

  // A trigger region is scriptable under its *saved* TriggerName — the list,
  // the other admins and the engine after a rejoin all read the saved row, so
  // that is the only name a script can be bound to from here.
  const isRegionType = !!entity && SCRIPTABLE_REGION_TYPES.has(entity.typeId);
  const isSavedRegion = isRegionType && !!entity?.saved;
  const schemaDeclaresTriggerName = declaresTriggerName(schema);
  const savedTriggerName = entity?.saved
    ? readTriggerName(entity.saved.properties)
    : '';
  // Keyed the way every AI-tree read is keyed — by the map's build identifier,
  // never its display name, which matches no build path and would widen the
  // region index to every map in the build.
  const map = useMemo<IAssistantMapRef | undefined>(
    () =>
      currentRoom?.map ??
      (assistant?.currentMapName
        ? { ueIdentifier: assistant.currentMapName }
        : undefined),
    [assistant?.currentMapName, currentRoom?.map],
  );
  const regionIndex = useRegionIndex(assistantMapKey(map));

  // The AI content bound to the saved name, matched the way the tree and the
  // runtime match it — by normalised name, never exact string — and scoped to
  // the configuration and map the editor has open.
  const { boundScripts, boundKnowledge } = useMemo<{
    boundScripts: IAssistantScript[];
    boundKnowledge: IAssistantRagVectorStorage[];
  }>(() => {
    if (!isSavedRegion || !savedTriggerName) {
      return { boundScripts: [], boundKnowledge: [] };
    }
    const scoped = filterByScope({
      scripts,
      ragItems,
      map,
      brandingId: currentBrandingId ?? undefined,
    });
    return {
      boundScripts: scoped.scripts.filter((script) =>
        triggersOf(script).some((trigger) =>
          triggerMatchesRegion(trigger, savedTriggerName),
        ),
      ),
      boundKnowledge: scoped.ragItems.filter((item) =>
        item.tags.some((tag) => tagMatchesTrigger(tag, savedTriggerName)),
      ),
    };
  }, [
    currentBrandingId,
    isSavedRegion,
    map,
    ragItems,
    savedTriggerName,
    scripts,
  ]);
  // Knowledge tagged for this region alone. An item also tagged for another
  // region still serves it and stays.
  const deletableKnowledge = useMemo(
    () =>
      boundKnowledge.filter((item) =>
        item.tags.every((tag) => tagMatchesTrigger(tag, savedTriggerName)),
      ),
    [boundKnowledge, savedTriggerName],
  );

  // Every region name that is not this entity's own, lower-cased: the build's
  // and the standing rooms' regions, and the other saved spawned triggers. A
  // dynamic row is left out — under a spawned name it is this trigger's own
  // walk-in registration, not another volume.
  const otherRegionNamesLower = useMemo(() => {
    const names = new Set(
      regionIndex.buildRegionNames.map((name) => name.toLowerCase()),
    );
    for (const other of realtimeEntities) {
      if (
        other.entityId !== entityId &&
        SCRIPTABLE_REGION_TYPES.has(other.typeId) &&
        other.saved
      ) {
        const name = readTriggerName(other.saved.properties);
        if (name) {
          names.add(name.toLowerCase());
        }
      }
    }
    return names;
  }, [entityId, realtimeEntities, regionIndex.buildRegionNames]);

  /**
   * Whether a name also belongs to a region other than this entity.
   *
   * @param name - Trigger name to check, compared trimmed and without case.
   * @returns True when a build, standing or other saved spawned region answers
   * to the name; false for a blank name or a dynamic row alone.
   */
  const isNameSharedWithAnotherRegion = useCallback(
    (name: string): boolean => {
      const lower = name.trim().toLowerCase();
      return !!lower && otherRegionNamesLower.has(lower);
    },
    [otherRegionNamesLower],
  );

  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);

  const [positionInputs, setPositionInputs] = useState(() =>
    deriveAxesInputs(entity?.position, 0, 2),
  );
  const [rotationInputs, setRotationInputs] = useState(() =>
    deriveAxesInputs(entity?.rotation, 0, 2),
  );
  const [scaleInputs, setScaleInputs] = useState(() =>
    deriveAxesInputs(entity?.scale, SCALE_DEFAULT, 2),
  );
  const [propertyInputs, setPropertyInputs] = useState<Record<string, string>>(
    () => seedAllPropertyInputs(schema, entity?.properties),
  );
  const [nameInput, setNameInput] = useState(() => entity?.name ?? '');

  /**
   * Re-seeds the name field when the entity is renamed elsewhere — a reload
   * after another admin's save carries their label, and an initial-only field
   * would write this client's stale one back on the next save.
   */
  const seededNameRef = useRef(entity?.name);
  useEffect(() => {
    if (entity?.name === undefined || entity.name === seededNameRef.current) {
      return;
    }
    seededNameRef.current = entity.name;
    setNameInput(entity.name);
  }, [entity?.name]);

  // The in-flight save, so a delete cannot race ahead of it and leave the row
  // it was about to write behind on the backend.
  const pendingSaveRef = useRef<Promise<unknown> | null>(null);
  // Which request is in flight, so its button shows a spinner and neither
  // action can be fired twice while the first is still going.
  const [pendingAction, setPendingAction] = useState<'save' | 'delete' | null>(
    null,
  );

  const transformInputs: Record<
    TransformGroup,
    { x: string; y: string; z: string }
  > = {
    position: positionInputs,
    rotation: rotationInputs,
    scale: scaleInputs,
  };
  const transformSetters: Record<
    TransformGroup,
    React.Dispatch<React.SetStateAction<{ x: string; y: string; z: string }>>
  > = {
    position: setPositionInputs,
    rotation: setRotationInputs,
    scale: setScaleInputs,
  };

  /**
   * Re-seeds the property inputs when the schema resolves after mount — the
   * client record can be mid-refresh when the entity is selected, and inputs
   * seeded from an absent schema would otherwise commit empty values over the
   * entity's real ones.
   */
  const schemaSeededRef = useRef(!!schema);
  useEffect(() => {
    if (schemaSeededRef.current || !schema) {
      return;
    }
    schemaSeededRef.current = true;
    setPropertyInputs(seedAllPropertyInputs(schema, entity?.properties));
    // entity?.properties is deliberately read at seed time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  /**
   * Runs the in-world gizmo session for the lifetime of this selection, but
   * only while the entity is actually tracked — starting a session for an
   * unknown id leaves an engine-side gizmo nothing can end. UE never ends the
   * session on its own; switching to another entity remounts this component,
   * so the end-then-start pair falls out of mount/unmount.
   */
  useEffect(() => {
    if (!entityExists) {
      return undefined;
    }
    startRealtimeEntityEditInUnreal(sendMessageToUnreal, entityId);
    return () => {
      endRealtimeEntityEditInUnreal(sendMessageToUnreal, entityId);
    };
  }, [entityId, entityExists, sendMessageToUnreal]);

  /**
   * Live-syncs the transform inputs from a settled in-world gizmo drag. This
   * deliberately overwrites: a drag the author just performed is the new
   * authoritative transform, not background noise to protect keystrokes from.
   * The nonce ref keeps a report retained from before this mount — already
   * superseded by whatever the registry seeded — from being replayed.
   */
  const appliedLiveTransformNonceRef = useRef(
    realtimeEntityLiveTransform?.nonce ?? 0,
  );
  useEffect(() => {
    if (
      !realtimeEntityLiveTransform ||
      realtimeEntityLiveTransform.entityId !== entityId ||
      realtimeEntityLiveTransform.nonce === appliedLiveTransformNonceRef.current
    ) {
      return;
    }
    appliedLiveTransformNonceRef.current = realtimeEntityLiveTransform.nonce;
    if (realtimeEntityLiveTransform.position) {
      setPositionInputs(
        deriveAxesInputs(realtimeEntityLiveTransform.position, 0, 2),
      );
    }
    if (realtimeEntityLiveTransform.rotation) {
      setRotationInputs(
        deriveAxesInputs(realtimeEntityLiveTransform.rotation, 0, 2),
      );
    }
    if (realtimeEntityLiveTransform.scale) {
      setScaleInputs(
        deriveAxesInputs(realtimeEntityLiveTransform.scale, SCALE_DEFAULT, 2),
      );
    }
  }, [realtimeEntityLiveTransform, entityId]);

  /**
   * Parses one transform group's inputs and sends only that group. An
   * unparseable axis reverts the whole group to the stored value without
   * sending — committing a fallback the user never typed would teleport the
   * entity — and an unchanged tuple sends nothing at all.
   *
   * @param group - Which transform group the blurred field belongs to.
   */
  const commitTransformGroup = useCallback(
    (group: TransformGroup) => {
      const { fallback } = TRANSFORM_GROUP_CONFIG[group];
      const inputs = transformInputs[group];
      const stored = entity?.[group];

      const parsed = [inputs.x, inputs.y, inputs.z].map((raw) =>
        parseFloat(raw),
      );
      if (parsed.some((axis) => !Number.isFinite(axis))) {
        transformSetters[group](deriveAxesInputs(stored, fallback, 2));
        return;
      }

      const storedTuple = toTuple(stored, fallback);
      if (parsed.every((axis, index) => axis === storedTuple[index])) {
        return;
      }

      updateRealtimeEntityInUnreal(sendMessageToUnreal, {
        entityId,
        [group]: parsed,
      });
      upsertRealtimeEntity({ entityId, [group]: parsed });
    },
    // transformInputs/transformSetters are rebuilt per render from the same
    // state hooks, so the underlying deps are the three input states.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      entity,
      entityId,
      positionInputs,
      rotationInputs,
      scaleInputs,
      sendMessageToUnreal,
      upsertRealtimeEntity,
    ],
  );

  /**
   * Encodes one property's raw input per its schema type and sends a
   * `properties` object carrying only that key. Unchanged values send
   * nothing; an unparseable number reverts the input to the stored value.
   *
   * @param property - Schema entry for the property being committed.
   * @param rawOverride - The just-set raw value for controls (checkbox,
   * select) that commit on change rather than on blur.
   * @param axesOverride - The just-picked tuple, for the colour picker, whose
   * channels are not in the input state yet when it commits.
   */
  const commitProperty = useCallback(
    (
      property: ISpawnableObjectProperty,
      rawOverride?: string,
      axesOverride?: number[],
    ) => {
      const stored = entity?.properties?.[property.name];
      let value: RealtimeEntityPropertyValue;

      if (isStructProperty(property)) {
        if (axesOverride) {
          value = axesOverride;
        } else {
          const axisCount = structAxisCount(property, stored);
          const axes: number[] = [];
          for (let axis = 0; axis < axisCount; axis += 1) {
            const parsed = parseFloat(
              propertyInputs[`${property.name}.${axis}`] ?? '',
            );
            if (!Number.isFinite(parsed)) {
              setPropertyInputs((prev) => ({
                ...prev,
                ...seedPropertyInputs(property, stored),
              }));
              return;
            }
            axes.push(parsed);
          }
          value = axes;
        }
      } else if (property.type === 'bool') {
        value = (rawOverride ?? propertyInputs[property.name]) === 'true';
      } else if (property.type === 'int' || property.type === 'float') {
        const raw = rawOverride ?? propertyInputs[property.name];
        const parsed = parseFloat(raw);
        if (!Number.isFinite(parsed)) {
          setPropertyInputs((prev) => ({
            ...prev,
            ...seedPropertyInputs(property, stored),
          }));
          return;
        }
        value = property.type === 'int' ? Math.trunc(parsed) : parsed;
        // The displayed text must match what was committed — '3.7' on an int
        // property commits 3 and shows 3.
        setPropertyInputs((prev) => ({
          ...prev,
          [property.name]: String(value),
        }));
      } else {
        value = rawOverride ?? propertyInputs[property.name] ?? '';
        // The region name is an identity every reader trims before comparing;
        // stored with stray whitespace it would match none of them.
        if (property.name === TRIGGER_NAME_PROPERTY) {
          value = value.trim();
          setPropertyInputs((prev) => ({
            ...prev,
            [property.name]: value as string,
          }));
        }
      }

      const isUnchanged = Array.isArray(value)
        ? Array.isArray(stored) &&
          stored.length === value.length &&
          value.every((axis, index) => axis === stored[index])
        : value === stored;
      if (isUnchanged) {
        return;
      }

      updateRealtimeEntityInUnreal(sendMessageToUnreal, {
        entityId,
        properties: { [property.name]: value },
      });
      upsertRealtimeEntity({
        entityId,
        properties: { [property.name]: value },
      });
    },
    [
      entity,
      entityId,
      propertyInputs,
      sendMessageToUnreal,
      upsertRealtimeEntity,
    ],
  );

  /**
   * Renames the entity in the session registry. Hub-side only — the wire
   * contract has no name field — so nothing is sent; an emptied field reverts
   * to the current name rather than storing a blank label.
   */
  const commitName = useCallback(() => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setNameInput(entity?.name ?? '');
      return;
    }
    if (trimmed === entity?.name) {
      return;
    }
    upsertRealtimeEntity({ entityId, name: trimmed });
  }, [entity, entityId, nameInput, upsertRealtimeEntity]);

  /**
   * Persists the entity's current state — the hotspot-style explicit Save. The
   * engine already lives with the latest edits (they went out over the socket
   * as they were made); this fixes the snapshot in the backend so it survives
   * the session and shows for every admin.
   */
  const handleSave = useCallback(async () => {
    if (!entity || !currentRoom?.roomId || !brandingId || pendingAction) {
      return;
    }

    // A rename still in the field is part of what the author asked to save;
    // waiting for the blur to commit it first would drop the click.
    const name = nameInput.trim() || entity.name;
    const snapshot = buildRealtimeEntitySnapshot({ ...entity, name });

    setPendingAction('save');
    setAutosaveStatus('saving');
    const save = SpawnedEntitiesService.saveSpawnedEntity(
      currentRoom.roomId,
      brandingId,
      snapshot,
    );
    pendingSaveRef.current = save;

    try {
      await save;
      upsertRealtimeEntity({ entityId, name, saved: snapshot });
      setAutosaveStatus('saved');
    } catch (saveError) {
      console.error(
        `Failed to save spawned entity: ${(saveError as Error)?.message}`,
        { roomId: currentRoom.roomId, brandingId, entityId },
      );
      setAutosaveStatus('error');
    } finally {
      if (pendingSaveRef.current === save) {
        pendingSaveRef.current = null;
      }
      setPendingAction(null);
    }
  }, [
    brandingId,
    currentRoom,
    entity,
    entityId,
    nameInput,
    pendingAction,
    setAutosaveStatus,
    upsertRealtimeEntity,
  ]);

  // What a delete of this region would take with it, and why it must not.
  const hasBoundContent =
    isSavedRegion && (boundScripts.length > 0 || boundKnowledge.length > 0);
  const cascadeBlocker: CascadeBlocker | null = useMemo(() => {
    if (!isSavedRegion) {
      return null;
    }
    if (!currentBrandingId) {
      return 'noBranding';
    }
    if (isNameSharedWithAnotherRegion(savedTriggerName)) {
      return 'sharedName';
    }
    if (
      boundScripts.some((script) =>
        triggersOf(script).some(
          (trigger) => !triggerMatchesRegion(trigger, savedTriggerName),
        ),
      )
    ) {
      return 'multiTrigger';
    }
    return null;
  }, [
    boundScripts,
    currentBrandingId,
    isNameSharedWithAnotherRegion,
    isSavedRegion,
    savedTriggerName,
  ]);
  const canCascade = isSavedRegion && !cascadeBlocker;

  /**
   * Deletes the entity: its bound scripts, knowledge and walk-in dynamic row
   * first when the cascade may run, then the saved snapshot when one can exist,
   * then the entity in-world; finally forgets it locally so its id is free.
   *
   * The cascade goes first because a script left behind without its region is
   * exactly what this delete exists to prevent, and the backend row goes before
   * the world because a row whose DELETE failed is what made a deleted object
   * come back on the next load. A delete that finds its target already gone
   * counts as done, so a retry after a partial failure can finish the job; a
   * failed cascade reloads the AI data so the next attempt sees what is left.
   */
  const performDelete = useCallback(async () => {
    if (pendingAction) {
      return;
    }

    const roomId = currentRoom?.roomId;
    const mapId = currentRoom?.map?.mapId;
    const pendingSave = pendingSaveRef.current;
    const mayExistOnBackend = !!entity?.saved || !!pendingSave;

    setPendingAction('delete');

    if (pendingSave) {
      await pendingSave.catch(() => undefined);
    }

    // The walk-in row goes whenever the region does — it is this trigger's
    // own registration — unless another region answers to the name, in which
    // case the row may be that region's.
    const dynamicRow =
      isSavedRegion && cascadeBlocker !== 'sharedName'
        ? dynamicAiTriggers.find(
            (trigger) =>
              trigger.triggerName.toLowerCase() ===
              savedTriggerName.toLowerCase(),
          )
        : undefined;
    const scriptsToDelete = canCascade ? boundScripts : [];
    const knowledgeToDelete = canCascade ? deletableKnowledge : [];
    const touchesAiContent =
      scriptsToDelete.length > 0 ||
      knowledgeToDelete.length > 0 ||
      !!dynamicRow;

    if (touchesAiContent) {
      try {
        await Promise.all([
          ...scriptsToDelete.map((script) =>
            settledIfMissing(
              AssistantScriptsService.deleteAssistantScript(script.scriptId),
            ),
          ),
          ...knowledgeToDelete.map((item) =>
            settledIfMissing(
              AssistantRagStorageService.deleteAssistantRagStorageItem(
                item.ragVectorLinkId,
              ),
            ),
          ),
          dynamicRow && mapId
            ? settledIfMissing(
                DynamicMapTriggersService.deleteDynamicMapTrigger(
                  mapId,
                  dynamicRow.triggerName,
                ),
              )
            : Promise.resolve(),
        ]);
        if (dynamicRow) {
          setDynamicAiTriggers((prev) =>
            prev.filter(
              (trigger) => trigger.triggerName !== dynamicRow.triggerName,
            ),
          );
        }
      } catch (cascadeError) {
        console.error(
          `Failed to delete the AI content bound to a spawned entity: ${
            (cascadeError as Error)?.message
          }`,
          {
            roomId,
            brandingId,
            entityId,
            scriptId: scriptsToDelete[0]?.scriptId,
          },
        );
        setAutosaveStatus('error');
        setPendingAction(null);
        setCascadeDialogOpen(false);
        void refetchAll();
        return;
      }
    }

    if (mayExistOnBackend && roomId && brandingId) {
      try {
        await SpawnedEntitiesService.deleteSpawnedEntity(
          roomId,
          brandingId,
          entityId,
        );
      } catch (deleteError) {
        // Already gone — never saved, or another admin deleted it first.
        if (!isMissingOnBackend(deleteError)) {
          console.error(
            `Failed to delete saved spawned entity: ${
              (deleteError as Error)?.message
            }`,
            { roomId, brandingId, entityId },
          );
          setAutosaveStatus('error');
          setPendingAction(null);
          setCascadeDialogOpen(false);
          return;
        }
      }
    }

    deleteRealtimeEntityInUnreal(sendMessageToUnreal, entityId);
    removeRealtimeEntity(entityId);
    setCascadeDialogOpen(false);
    clearSelection();
    if (touchesAiContent) {
      void refetchAll();
    }
  }, [
    boundScripts,
    brandingId,
    canCascade,
    cascadeBlocker,
    clearSelection,
    currentRoom,
    deletableKnowledge,
    dynamicAiTriggers,
    entity,
    entityId,
    isSavedRegion,
    pendingAction,
    refetchAll,
    removeRealtimeEntity,
    savedTriggerName,
    sendMessageToUnreal,
    setAutosaveStatus,
    setDynamicAiTriggers,
  ]);

  /**
   * The Delete button: asks first when the region carries a script or
   * knowledge — those go with it, or stay for a reason the dialog names — and
   * deletes outright when nothing is bound.
   */
  const handleDelete = useCallback(() => {
    if (pendingAction) {
      return;
    }
    if (hasBoundContent) {
      setCascadeDialogOpen(true);
      return;
    }
    void performDelete();
  }, [hasBoundContent, pendingAction, performDelete]);

  /**
   * Jumps to the AI-scripts tree with this region selected, so a script can be
   * created for it without hunting the row down by name.
   */
  const handleAssignScript = useCallback(() => {
    if (!savedTriggerName) {
      return;
    }
    openRegionScript(savedTriggerName);
  }, [openRegionScript, savedTriggerName]);

  // Memoised: the dirty check stringifies the entity twice, and it is read on
  // every render of the panel.
  const isDirty = useMemo(
    () => (entity ? isRealtimeEntityDirty(entity) : false),
    [entity],
  );
  const trimmedName = nameInput.trim();
  const hasPendingRename =
    !!entity && !!trimmedName && trimmedName !== entity.name;

  const setPropertyInput = useCallback((key: string, raw: string) => {
    setPropertyInputs((prev) => ({ ...prev, [key]: raw }));
  }, []);

  // The region name as typed, and what its state means for scripting.
  const liveTriggerName = (propertyInputs[TRIGGER_NAME_PROPERTY] ?? '').trim();
  const isTriggerNameSaved =
    !!entity?.saved &&
    !!savedTriggerName &&
    liveTriggerName === savedTriggerName;
  // The scripts a pending rename would leave behind: those bound to the saved
  // name while the field holds a different one.
  const scriptsOnSavedName =
    savedTriggerName && liveTriggerName !== savedTriggerName
      ? boundScripts.length
      : 0;
  const triggerNameHint = useMemo<{
    text: string;
    tone: 'hint' | 'warning' | 'error';
  } | null>(() => {
    if (!isRegionType || !schemaDeclaresTriggerName) {
      return null;
    }
    if (!liveTriggerName) {
      return {
        tone: 'error',
        text: translate(
          'editorMode.realtimeEntityTriggerNameBlank',
          'Unnamed regions cannot have an AI script',
        ),
      };
    }
    if (scriptsOnSavedName > 0) {
      return {
        tone: 'warning',
        text: translate('editorMode.realtimeEntityTriggerNameRenameOrphans', {
          count: scriptsOnSavedName,
          defaultValue:
            '{{count}} script(s) are bound to the current name and will stop firing after Save',
        }),
      };
    }
    if (isNameSharedWithAnotherRegion(liveTriggerName)) {
      return {
        tone: 'warning',
        text: translate(
          'editorMode.realtimeEntityTriggerNameCollision',
          'Another region already uses this name; a script bound to it fires for both',
        ),
      };
    }
    if (!isTriggerNameSaved) {
      return {
        tone: 'hint',
        text: translate(
          'editorMode.realtimeEntityTriggerNameUnsaved',
          'Save the object — the script list follows the saved name',
        ),
      };
    }
    return null;
  }, [
    isNameSharedWithAnotherRegion,
    isRegionType,
    isTriggerNameSaved,
    liveTriggerName,
    schemaDeclaresTriggerName,
    scriptsOnSavedName,
    translate,
  ]);
  const canAssignScript =
    isRegionType &&
    schemaDeclaresTriggerName &&
    isTriggerNameSaved &&
    !pendingAction;

  if (!entity) {
    return (
      <Box ref={rootRef} sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate(
            'editorMode.realtimeEntityGone',
            'This object is no longer tracked — it was deleted or the room changed.',
          )}
        </Typography>
      </Box>
    );
  }

  /**
   * Renders one transform group as three labelled per-axis number fields.
   *
   * @param group - Which transform group to render.
   * @param label - Section heading above the fields.
   */
  const renderTransformGroup = (group: TransformGroup, label: string) => (
    <Box sx={styles.fieldCard}>
      <Typography sx={styles.fieldCardLabel}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: '6px' }}>
        {(['x', 'y', 'z'] as const).map((axis, index) => (
          <TextField
            key={axis}
            value={transformInputs[group][axis]}
            onChange={(e) =>
              transformSetters[group]((prev) => ({
                ...prev,
                [axis]: e.target.value,
              }))
            }
            onBlur={() => commitTransformGroup(group)}
            size="small"
            fullWidth
            type="number"
            autoComplete="off"
            label={TRANSFORM_GROUP_CONFIG[group].axisLabels[index]}
            inputProps={{
              step: 0.01,
              'data-testid': `realtime-entity-${group}-${axis}-input`,
            }}
            sx={styles.textField}
          />
        ))}
      </Box>
    </Box>
  );

  /**
   * Renders the control a schema property's type calls for: checkbox, enum
   * select, per-component number fields, or a plain text/number field.
   *
   * @param property - Schema entry for the property.
   */
  const renderPropertyControl = (property: ISpawnableObjectProperty) => {
    if (property.type === 'bool') {
      return (
        <Box sx={styles.propertyToggleRow}>
          <Typography sx={styles.propertyToggleLabel}>
            {property.displayName}
          </Typography>
          <Switch
            size="small"
            checked={propertyInputs[property.name] === 'true'}
            onChange={(e) => {
              const raw = e.target.checked ? 'true' : 'false';
              setPropertyInput(property.name, raw);
              commitProperty(property, raw);
            }}
            inputProps={
              {
                'aria-label': property.displayName,
                'data-testid': `realtime-entity-prop-${property.name}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        </Box>
      );
    }

    if (property.type === 'enum') {
      return (
        <TextField
          select
          value={propertyInputs[property.name] ?? ''}
          onChange={(e) => {
            setPropertyInput(property.name, e.target.value);
            commitProperty(property, e.target.value);
          }}
          size="small"
          fullWidth
          data-testid={`realtime-entity-prop-${property.name}`}
          sx={styles.textField}
        >
          {(property.options ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (isStructProperty(property)) {
      const stored = entity?.properties?.[property.name];
      const axisCount = structAxisCount(property, stored);
      const axisLabels = structAxisLabels(property, axisCount);

      const readChannel = (axis: number): number => {
        const parsed = parseFloat(
          propertyInputs[`${property.name}.${axis}`] ?? '',
        );

        return Number.isFinite(parsed) ? parsed : 0;
      };

      const isColor = property.type === 'color';
      const channels = isColor ? [0, 1, 2].map(readChannel) : [];
      // Read off the live inputs rather than the stored value, so correcting a
      // channel by hand also corrects the scale the picker writes in.
      const byteScale = isColor
        ? isByteScaleColor(channels) || isByteScaleColor(stored)
        : false;

      /**
       * Writes a picked colour into the channel inputs and commits it, leaving
       * an existing alpha alone.
       */
      const commitPickedColor = (hex: string) => {
        const picked = hexToColorChannels(hex, byteScale);

        setPropertyInputs((prev) => ({
          ...prev,
          ...Object.fromEntries(
            picked.map((channel, axis) => [
              `${property.name}.${axis}`,
              String(channel),
            ]),
          ),
        }));

        commitProperty(property, undefined, [
          ...picked,
          ...(axisCount === 4 ? [readChannel(3)] : []),
        ]);
      };

      return (
        <Box>
          {isColor ? (
            <ColorPropertyControl
              label={property.displayName}
              hex={colorTupleToHex(channels, byteScale)}
              onChange={commitPickedColor}
              testId={`realtime-entity-prop-${property.name}-picker`}
            />
          ) : (
            <Typography sx={styles.fieldLabel}>
              {property.displayName}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: '6px' }}>
            {Array.from({ length: axisCount }, (_, axis) => (
              <TextField
                key={axis}
                value={propertyInputs[`${property.name}.${axis}`] ?? ''}
                onChange={(e) =>
                  setPropertyInput(`${property.name}.${axis}`, e.target.value)
                }
                onBlur={() => commitProperty(property)}
                size="small"
                fullWidth
                type="number"
                autoComplete="off"
                label={axisLabels[axis]}
                inputProps={{
                  step: 0.01,
                  'data-testid': `realtime-entity-prop-${property.name}-${axis}`,
                }}
                sx={styles.textField}
              />
            ))}
          </Box>
        </Box>
      );
    }

    const isTriggerNameField =
      isRegionType && property.name === TRIGGER_NAME_PROPERTY;
    const hint = isTriggerNameField ? triggerNameHint : null;
    const hintStyle =
      hint?.tone === 'error'
        ? styles.helperError
        : hint?.tone === 'warning'
        ? styles.helperWarning
        : styles.helperHint;

    return (
      <>
        <TextField
          value={propertyInputs[property.name] ?? ''}
          onChange={(e) => setPropertyInput(property.name, e.target.value)}
          onBlur={() => commitProperty(property)}
          size="small"
          fullWidth
          type={
            property.type === 'int' || property.type === 'float'
              ? 'number'
              : 'text'
          }
          autoComplete="off"
          inputProps={{
            'data-testid': `realtime-entity-prop-${property.name}`,
          }}
          sx={styles.textField}
        />
        {hint ? (
          <Typography
            sx={hintStyle}
            data-testid="realtime-entity-trigger-name-hint"
            data-tone={hint.tone}
          >
            {hint.text}
          </Typography>
        ) : null}
        {isTriggerNameField ? (
          <Button
            onClick={handleAssignScript}
            startIcon={<AutoAwesomeOutlinedIcon />}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!canAssignScript}
            sx={styles.assignScriptButton}
            data-testid="realtime-entity-assign-script-btn"
          >
            {translate(
              'editorMode.realtimeEntityAssignScript',
              'Assign AI script',
            )}
          </Button>
        ) : null}
      </>
    );
  };

  /**
   * The confirm behind a delete that would take AI content along — or that
   * leaves it behind for a reason the author should see before agreeing.
   */
  const renderCascadeDialog = () => {
    const knowledgeCount = deletableKnowledge.length;
    const boundScript = boundScripts[0] ?? null;
    const scriptTitles = boundScripts
      .map((script) => script.title || savedTriggerName)
      .join(', ');
    const blockerNote =
      cascadeBlocker === 'sharedName'
        ? translate('editorMode.realtimeEntityDeleteSharedNameNote', {
            region: savedTriggerName,
            defaultValue:
              'The script and knowledge stay: another region also uses the name "{{region}}".',
          })
        : cascadeBlocker === 'multiTrigger'
        ? translate(
            'editorMode.realtimeEntityDeleteMultiTriggerNote',
            'The script and knowledge stay: the script also serves other triggers.',
          )
        : cascadeBlocker === 'noBranding'
        ? translate(
            'editorMode.realtimeEntityDeleteNoBrandingNote',
            'The script and knowledge stay: no configuration is selected, so they cannot be matched safely.',
          )
        : null;
    const body = boundScript
      ? translate('editorMode.realtimeEntityDeleteCascadeBody', {
          script: scriptTitles,
          count: knowledgeCount,
          defaultValue:
            'This deletes the object, its script "{{script}}" and {{count}} knowledge item(s) bound to it. This can\'t be undone.',
        })
      : translate('editorMode.realtimeEntityDeleteCascadeBodyNoScript', {
          count: knowledgeCount,
          defaultValue:
            "This deletes the object and {{count}} knowledge item(s) bound to it. This can't be undone.",
        });

    return (
      <Dialog
        open={cascadeDialogOpen}
        onClose={() => setCascadeDialogOpen(false)}
        data-testid="realtime-entity-delete-cascade-dialog"
        PaperProps={{ sx: styles.confirmDialogPaper }}
      >
        <DialogTitle>
          {blockerNote
            ? translate('editorMode.realtimeEntityDelete', 'Delete object')
            : translate(
                'editorMode.realtimeEntityDeleteCascadeTitle',
                'Delete object and its AI script?',
              )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="realtime-entity-delete-cascade-body">
            {blockerNote ?? body}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCascadeDialogOpen(false)}
            disabled={pendingAction === 'delete'}
            data-testid="realtime-entity-delete-cascade-cancel-btn"
          >
            {translate('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            onClick={() => void performDelete()}
            disabled={pendingAction === 'delete'}
            data-testid="realtime-entity-delete-cascade-confirm-btn"
          >
            {translate('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box ref={rootRef} sx={styles.panelColumn}>
      <Box sx={styles.panelScroll}>
        <Box sx={styles.fieldCardStack}>
          <Box sx={styles.fieldCard}>
            <Typography sx={styles.fieldCardLabel}>
              {translate('editorMode.hotspotName', 'Name')}
            </Typography>
            <TextField
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={commitName}
              size="small"
              fullWidth
              autoComplete="off"
              inputProps={{ 'data-testid': 'realtime-entity-name-input' }}
              sx={styles.textField}
            />
          </Box>

          {renderTransformGroup(
            'position',
            translate('editorMode.hotspotLocation', 'Location'),
          )}
          {renderTransformGroup(
            'rotation',
            translate('editorMode.hotspotRotation', 'Rotation'),
          )}
          {renderTransformGroup(
            'scale',
            translate('editorMode.hotspotScale', 'Scale'),
          )}

          {isRegionType && !!schema && !schemaDeclaresTriggerName ? (
            <Typography
              sx={styles.helperWarning}
              data-testid="realtime-entity-trigger-name-hint"
              data-tone="warning"
            >
              {translate(
                'editorMode.realtimeEntityTriggerNameNotExported',
                'This build does not expose the region name; the object cannot carry an AI script',
              )}
            </Typography>
          ) : null}

          {schemaProperties.length > 0 && (
            <>
              <Typography sx={styles.sectionLabel}>
                {translate('editorMode.realtimeEntityProperties', 'Properties')}
              </Typography>
              {schemaProperties.map((property) => (
                <Box key={property.name} sx={styles.fieldCard}>
                  {needsExternalLabel(property) && (
                    <Typography sx={styles.fieldCardLabel}>
                      {property.displayName}
                    </Typography>
                  )}
                  {renderPropertyControl(property)}
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>

      <Box sx={styles.panelActions}>
        <Button
          onClick={handleSave}
          startIcon={
            pendingAction === 'save' ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          variant="contained"
          fullWidth
          disabled={
            !!pendingAction || !brandingId || (!isDirty && !hasPendingRename)
          }
          sx={styles.saveButton}
          data-testid="realtime-entity-save-btn"
        >
          {translate('editorMode.realtimeEntitySave', 'Save object')}
        </Button>
        <Button
          onClick={handleDelete}
          startIcon={
            pendingAction === 'delete' ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon />
            )
          }
          variant="outlined"
          fullWidth
          disabled={!!pendingAction}
          sx={styles.deleteButton}
          data-testid="realtime-entity-delete-btn"
        >
          {translate('editorMode.realtimeEntityDelete', 'Delete object')}
        </Button>
      </Box>
      {renderCascadeDialog()}
    </Box>
  );
};

export default RealtimeEntityInspector;
